/**
 * RestroPulse POS · Staff PIN login screen
 * ─────────────────────────────────────────────────────────────────────
 * Two authentication paths exist:
 *   • Real staff (default) — POST /api/pos/auth/login with outlet_id + PIN.
 *     Returns a POS session token (separate from the platform JWT). The
 *     token is persisted in localStorage under `pos_token` and read by
 *     every subsequent /api/pos/* call.
 *   • Demo mode (`/pos-demo`) — no backend call, resolves against the
 *     seeded mock staff. The demo PIN hint is shown ONLY in this mode.
 *
 * Failure messaging is neutral — never distinguish "wrong PIN" from
 * "unknown PIN" from "locked". After lockout, tell the user to contact
 * a manager.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { LockKeyhole, Delete, ArrowRight } from "lucide-react";
import { DEMO_STAFF_PIN } from "./mockData";
import { findStaffByPin } from "./posReducer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StaffLoginScreen({ demo = false, onUnlock, previouslyLoggedIn, outletName, outletId, businessDate, onRebind, rebindBusy, rebindError }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Clear a stale error message only when the user starts typing a
  // fresh PIN. `setPin("")` on a failed attempt would otherwise re-
  // trigger this and immediately wipe the error we just set — leaving
  // the operator with no idea why the login was refused.
  useEffect(() => { if (pin) setError(null); }, [pin]);

  const submit = async () => {
    if (pin.length < 4) return;
    if (demo) {
      // Demo path — resolve against seeded mock staff. /pos-demo NEVER
      // touches the real pos_staff collection.
      const staff = findStaffByPin(pin);
      if (!staff || !staff.active) {
        return setError("Incorrect PIN. If you have forgotten your PIN, ask your manager to issue a new one.");
      }
      onUnlock(staff);
      return;
    }
    // Real path — hit the backend.
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/pos/auth/login`, { outlet_id: outletId, pin });
      const { token, session } = r.data;
      window.localStorage.setItem("pos_token", token);
      onUnlock({
        id:       session.staff_id,
        name:     session.name,
        role:     session.role,
        outletId: session.outlet_id,
        active:   true,
      });
    } catch (e) {
      const s = e?.response?.status;
      if (s === 423) {
        // Rate-limit lockout is the ONE differentiated response — the
        // operator needs to know they must wait rather than keep
        // trying. Every other failure returns the neutral message so
        // an attacker cannot enumerate PINs, staff, or outlet identity.
        setError(e?.response?.data?.detail || "This entry is temporarily locked. Please contact a manager.");
      } else {
        // Neutral — server returns 401 for unknown PIN, wrong outlet,
        // deactivated staff, POS-disabled tenant, and malformed input,
        // with identical message and timing. We mirror that here.
        setError(e?.response?.data?.detail || "Incorrect PIN. If you have forgotten your PIN, ask your manager to issue a new one.");
      }
      setPin("");
    } finally {
      setSubmitting(false);
    }
  };

  const key = (v) => {
    if (v === "back") setPin((p) => p.slice(0, -1));
    else if (v === "clear") setPin("");
    else if (v === "enter") submit();
    else setPin((p) => (p + v).slice(0, 6));
  };

  useEffect(() => {
    const h = (e) => {
      if (e.key >= "0" && e.key <= "9") key(e.key);
      else if (e.key === "Backspace") key("back");
      else if (e.key === "Enter") key("enter");
      else if (e.key === "Escape") key("clear");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // `key` is defined in the render scope but is stable within a render;
    // we intentionally re-bind on every `pin` change so backspace applies
    // to the latest value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  // Outlet name — drop any "· <floor/zone>" suffix per the spec.
  const cleanOutletName = (outletName || "RestroPulse POS").split("·")[0].trim();

  return (
    <div className="fixed inset-0 z-[100] bg-[#0F1A16] flex items-center justify-center"
      data-testid="pos-staff-login">
      <div className="w-[400px] rounded-2xl bg-white shadow-2xl p-6 flex flex-col gap-4">
        <div className="flex items-center gap-2 text-[color:var(--color-primary)]">
          <LockKeyhole className="w-5 h-5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold">
              {previouslyLoggedIn ? "Terminal locked" : "Staff sign-in"}
            </div>
            <div className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">{cleanOutletName}</div>
          </div>
        </div>
        <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
          {businessDate ? <>Business day <strong className="tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]">{businessDate}</strong> · </> : null}
          {"Enter your 4-digit staff PIN to unlock the terminal."}
        </div>
        <div className="h-14 rounded-lg border-2 border-[color:var(--color-primary)] bg-[#F4F7F5] flex items-center justify-center text-[32px] tracking-[0.6em] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums"
          data-testid="pos-staff-login-pin-display">
          {pin ? "●".repeat(pin.length) : <span className="text-[14px] tracking-normal text-[color:var(--pos-color-secondary-text,#5C6B64)] font-normal">Enter PIN</span>}
        </div>
        {error && (
          <div className="text-[12px] text-rose-800 bg-rose-50 border border-rose-200 rounded p-2"
            data-testid="pos-staff-login-error">{error}</div>
        )}
        <div className="grid grid-cols-3 gap-2" data-testid="pos-staff-login-keypad">
          {["1","2","3","4","5","6","7","8","9","clear","0","back"].map((k) => (
            <button key={k} type="button" onClick={() => key(k)}
              data-testid={`pos-staff-login-key-${k}`}
              className={`h-14 rounded-lg text-[18px] font-semibold border transition
                ${k === "clear" || k === "back"
                  ? "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"
                  : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[#F4F7F5]"}`}>
              {k === "back" ? <Delete className="w-5 h-5 mx-auto" /> : k === "clear" ? "C" : k}
            </button>
          ))}
        </div>
        <button type="button" onClick={submit} disabled={pin.length < 4 || submitting}
          data-testid="pos-staff-login-submit"
          className="h-12 rounded-lg bg-[color:var(--color-primary)] text-white font-semibold text-[14px] hover:brightness-110 disabled:opacity-40 flex items-center justify-center gap-2">
          {submitting ? "Signing in…" : "Unlock terminal"} <ArrowRight className="w-4 h-4" />
        </button>
        {demo && (
          <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 text-center"
            data-testid="pos-staff-login-demo-hint">
            Demo cashier PIN · <strong className="tabular-nums">{DEMO_STAFF_PIN}</strong>
          </div>
        )}
        {onRebind && !demo && (
          <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] text-center border-t border-[color:var(--pos-color-border,#EEF1EE)] pt-3" data-testid="pos-staff-login-rebind-block">
            This device is bound to <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{cleanOutletName}</strong>.
            <button type="button" onClick={onRebind} disabled={!!rebindBusy}
              data-testid="pos-staff-login-rebind"
              className="ml-1 underline text-[color:var(--color-primary)] hover:brightness-110 disabled:opacity-40">
              Rebind to another outlet
            </button>
            {rebindError ? (
              <div className="mt-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 text-left"
                data-testid="pos-staff-login-rebind-error">{rebindError}</div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
